import {
  Children,
  forwardRef,
  isValidElement,
  useId,
  type ReactElement,
} from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { _useAriaLabelingProps } from '@dynatrace/strato-components/core';
import { Flex, useBreakpoint } from '@dynatrace/strato-components/layouts';

import { Content, type InformationContentProps } from './Content.js';
import { InformationOverlayContext } from './contexts/InformationOverlayContext.js';
import { InformationOverlayIcon } from './InformationOverlayIcon.js';
import { Trigger, type InformationOverlayTriggerProps } from './Trigger.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { useOverlayWithTrigger } from '../hooks/useOverlayWithTrigger.js';
import { Overlay } from '../overlay/Overlay.js';
import { basePlacementsType } from '../types/placement.js';

// #region Typings
/**
 * Accepted properties for InformationOverlay
 * @public
 */
export interface InformationOverlayBaseProps
  extends AriaLabelingProps,
    WithChildren,
    DataTestId,
    MaskingProps {
  /**
   * The preferred placement for the overlay if there is enough space. If the preferred placement is not possible, the overlay will be positioned according to the available space.
   * @defaultValue 'bottom'
   */
  placement?: basePlacementsType;
  /**
   * Controls the color of the styling and the trigger icon.
   * @defaultValue 'neutral' or a container's color.
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
}

/**
 * @public
 */
export interface InformationOverlayUncontrolledProps {
  /**
   * Determines whether or not the overlay is initially opened in an uncontrolled scenario.
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * Determines whether or not the overlay is open in a controlled scenario.
   */
  open?: never;
  /**
   * Callback fired when the open attribute changes in a controlled scenario.
   */
  onOpenChange?: never;
}

/**
 * @public
 */
export interface InformationOverlayControlledProps {
  /**
   * Determines whether or not the overlay is initially opened in an uncontrolled scenario.
   */
  defaultOpen?: never;
  /**
   * Determines whether or not the overlay is open in a controlled scenario.
   */
  open: boolean;
  /**
   * Callback fired when the open attribute changes in a controlled scenario.
   */
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * Accepted properties for InformationOverlay
 * @public
 */
export type InformationOverlayProps = InformationOverlayBaseProps &
  (InformationOverlayUncontrolledProps | InformationOverlayControlledProps);

type InformationOverlaySlots = {
  trigger?: ReactElement<InformationOverlayTriggerProps>;
  content?: ReactElement<InformationContentProps>;
};
// #endregion Typings

const InformationOverlayRoot = forwardRef<
  HTMLButtonElement,
  InformationOverlayProps
>((props, forwardedRef) => {
  const {
    children,
    defaultOpen,
    open,
    onOpenChange,
    color = 'neutral',
    placement = 'bottom',
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const id = useId();
  const { 'aria-describedby': ariaDescribedBy, ...ariaProps } =
    _useAriaLabelingProps(remainingProps);

  const [isOpen, setIsOpen] = useControllableState({
    defaultValue: defaultOpen,
    value: open,
    onChange: onOpenChange,
  });

  const matchesMaxWidthWindow = useBreakpoint('(max-width: 480px)');

  const {
    targetRef,
    triggerRef,
    overlayRef,
    overlayState,
    overlayProps,
    overlayTriggerProps,
  } = useOverlayWithTrigger({
    placement: `${placement}-start`,
    placementFallbacks: [
      'top-start',
      'bottom-start',
      'right-start',
      'left-start',
    ],
    offset: 4,
    crossOffset: -16,
    widthStrategy: matchesMaxWidthWindow ? { max: 300 } : { max: 400 },
    open: isOpen,
    onOpenChange: setIsOpen,
    shouldCloseOnBlur: true,
  });

  const rootRef = useMergeRefs([forwardedRef, triggerRef, targetRef]);

  const slots: InformationOverlaySlots = {
    content: undefined,
    trigger: undefined,
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Trigger)) {
      slots.trigger = child;
    } else if (isSlot(child, Content)) {
      slots.content = child;
    } else if (isValidElement(child) && isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        'Only Trigger and Content are valid children of the InformationOverlay.',
      );
    }
  });

  return (
    <InformationOverlayContext.Provider
      value={{ color, overlayTriggerProps, id, isOpen }}
    >
      <Trigger
        ref={rootRef}
        id={slots.trigger?.props?.id}
        aria-label={slots.trigger?.props['aria-label']}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        {slots.trigger?.props.children}
      </Trigger>
      <Overlay
        ref={overlayRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        isOpen={overlayState.isOpen}
        overlayProps={overlayProps}
        aria-describedby={ariaDescribedBy ?? id}
        {...ariaProps}
      >
        <Flex paddingY={12} paddingX={16} flexDirection="column">
          {slots.content}
        </Flex>
      </Overlay>
    </InformationOverlayContext.Provider>
  );
});

(
  InformationOverlayRoot as typeof InformationOverlayRoot & {
    displayName: string;
  }
).displayName = 'InformationOverlay';

/**
 * The `InformationOverlay` provides additional information via an overlay, the position of which can be adjusted.
 * According to the type of information, the `variant` can be set. The trigger consists of an icon and an optional text.
 * By default, the `variant` is `neutral` and the overlay is positioned on the bottom.
 * @public
 */
export const InformationOverlay = Object.assign(InformationOverlayRoot, {
  Content,
  Trigger,
  Icon: InformationOverlayIcon,
});
